import React, { useState, useContext } from "react";
import {
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { blokkoColors } from '../../theme/blokkoColors';
import LoadingBackdrop from '../LoadingBackdrop'; 

import AuthContext from "../../context/AuthContext";
import { fetchData } from "../../services/apiService";
import { localDayStartToUtcDate } from "../../services/dateUtils";

const DailyIsoClearingSettlementReportForm = ({
  setSnackOpen,
  setMessageOpen,
  setSeverity,
}) => {
  const { token, acquirerData } = useContext(AuthContext);
  const [currency, setCurrency] = useState(acquirerData?.currency_code || "");
  const [date, setdate] = useState("");
  const [hide, setHide] = useState(true);
  const [reportData, setReportData] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const handleDateChange = (event) => {
    setdate(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setRetryCount(0);

    const timestamp = new Date().toISOString();
    const data = {
      version: "04.09.01",
      acquirer: {
        callbackURL: "https://webhook.site/dd4eadec-dab5-4397-9754-c45738f2fb9e",
        timeStamp: timestamp,
      },
      fiat: {
        currencyCode: currency,
      },
      report: {
        date: localDayStartToUtcDate(date, "YYYYMMDD"),
      },
    };

    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      acquirerName: acquirerData.acquirerName,
      acquirerID: acquirerData.acquirerId,
    };
    try {
      const response = await fetchData({
        endpoint: "/daily-isos-clearing-settlement",
        data,
        headers,
        token,
      });

      if (response && response.data && response.data.blokko) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const checkResponse = await fetchData({
          endpoint: "/daily-isos-clearing-settlement",
          data,
          headers,
          token,
        });
        setRetryCount(1)
        if (checkResponse && checkResponse.data.iso) {
          setReportData(checkResponse.data.iso);
          setHide(false);
          setSnackOpen(true);
          setMessageOpen("Report generated successfully!");
          setSeverity("success");
        } else {
          setSnackOpen(true);
          setMessageOpen("Report is still being generated. Please try again later.");
          setSeverity("info");
        }
      } else if (response && response.data.iso) {
        setReportData(response.data.iso);
        setHide(false);
        setSnackOpen(true);
        setMessageOpen("Report generated successfully!");
        setSeverity("success");
      } else {
        setSnackOpen(true);
        setReportData('');
        setMessageOpen("No transaction for this date");
        setSeverity("error");
      }
    } catch (error) {
      setSnackOpen(true);
      setMessageOpen("No transaction for this date");
      setReportData('');
      setSeverity("error");
    } finally {
      setLoading(false); 
    }
  };

  const downloadCSV = () => {
    const headers = [
      "Date",
      "Transactions Amount",
      "Transactions Count",
      "Amount Due",
      "Holding Name",
      "Holding ID",
      "Holding Transactions Amount",
      "Holding Transactions Count",
      "Holding Amount Due",
      "Group Name",
      "Group ID",
      "Group Transactions Amount",
      "Group Transactions Count",
      "Group Amount Due",
      "Group Tx MDR",
      "Group Tx Processing",
      "Merchant Name",
      "Merchant ID",
      "Merchant Amount Due",
      "Merchant Exchange Commission",
      "Merchant Fiat Conversion",
      "Merchant Transactions Amount",
      "Merchant Transactions Count",
      "Merchant Tx Processing",
      "Terminal ID", 
      "Terminal Exchange",
      "Terminal Amount Due",
      "Terminal Exchange Commission",
      "Terminal Fiat Conversion",
      "Terminal Transactions Amount",
      "Terminal Transactions Count",
      "Terminal Tx Processing"
    ];
  
    const formatDecimal = (value) => {

      if (typeof value === "string") {
        const cleanedValue = value.replace(",", ".").replace(/[^0-9.]/g, "");
    
        if ((cleanedValue.match(/\./g) || []).length > 1) {
          return "0.0000";
        }
    
        const number = parseFloat(cleanedValue);
  
        if (!isNaN(number)) {
          return number.toFixed(4);
        }
      }
    
      if (typeof value === "number") {
        return value.toFixed(4);
      }
    
      return "0.0000";
    };
    const rows = [];
  
    reportData.report.forEach((report) => {

      const date = `${report.date.toString().substring(4, 6)}/${report.date.toString().substring(6, 8)}/${report.date.toString().substring(0, 4)}`;

      report.holdings.holding.forEach((holding) => {
        holding.groups.group.forEach((group) => {
          group.merchants.merchant.forEach((merchant) => {
            if (merchant.terminals && merchant.terminals.terminal.length > 0) {
              merchant.terminals.terminal.forEach((terminal) => {
                rows.push([
                  date, 
                  formatDecimal(reportData.acquirer.transactionsAmount),
                  reportData.acquirer.transactionsCount,
                  formatDecimal(reportData.acquirer.amountDue),
                  holding.merchantHolding,
                  holding.merchantHoldingID,
                  formatDecimal(holding.transactionsAmount),
                  holding.transactionsCount,
                  formatDecimal(holding.amountDue),
                  group.merchantGroup,
                  group.merchantGroupID,
                  formatDecimal(group.transactionsAmount),
                  group.transactionsCount,
                  formatDecimal(group.amountDue),
                  formatDecimal(group.txMDR || "0.000"), 
                  formatDecimal(group.txProcessing || "0.000"),
                  merchant.name,
                  merchant.ID,
                  formatDecimal(merchant.amountDue),
                  formatDecimal(merchant.exchangeCommission),
                  formatDecimal(merchant.fiatConversion),
                  formatDecimal(merchant.transactionsAmount),
                  merchant.transactionsCount,
                  formatDecimal(merchant.txProcessing),
                  terminal.terminalID, 
                  terminal.exchangeID, 
                  formatDecimal(terminal.amountDue),
                  formatDecimal(terminal.exchangeCommission),
                  formatDecimal(terminal.fiatConversion),
                  formatDecimal(terminal.transactionsAmount),
                  terminal.transactionsCount,
                  formatDecimal(terminal.txProcessing)
                ]);
              });
            } else {
              rows.push([
                date, 
                formatDecimal(reportData.acquirer.transactionsAmount),
                reportData.acquirer.transactionsCount,
                formatDecimal(reportData.acquirer.amountDue),
                holding.merchantHolding,
                holding.merchantHoldingID,
                formatDecimal(holding.transactionsAmount),
                holding.transactionsCount,
                formatDecimal(holding.amountDue),
                group.merchantGroup,
                group.merchantGroupID,
                formatDecimal(group.transactionsAmount),
                group.transactionsCount,
                formatDecimal(group.amountDue),
                formatDecimal(group.txMDR || "0.000"), 
                formatDecimal(group.txProcessing || "0.000"),
                merchant.name,
                merchant.ID,
                formatDecimal(merchant.amountDue),
                formatDecimal(merchant.exchangeCommission),
                formatDecimal(merchant.fiatConversion),
                formatDecimal(merchant.transactionsAmount),
                merchant.transactionsCount,
                formatDecimal(merchant.txProcessing),
                "", 
                "", 
                "", 
                "", 
                "", 
                "", 
                ""  
              ]);
            }
          });
        });
      });
    });
  
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");
  
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "ClearingSettlementReport.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const renderHoldingsTable = (reportData) => {

    return (
      <Accordion style={{ marginTop: "20px" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Report Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="h6">ISO Details</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ISO Name</TableCell>
                  <TableCell>ISO ID</TableCell>
                  <TableCell>Transactions Count</TableCell>
                  <TableCell>Transactions Amount</TableCell>
                  <TableCell>Exchange Commission</TableCell>
                  <TableCell>Fiat Conversion</TableCell>
                  <TableCell>Amount Due</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.isoName}</TableCell>
                    <TableCell>{item.isoID}</TableCell>
                    <TableCell>{item.transactionsCount}</TableCell>
                    <TableCell>{item.transactionsAmount}</TableCell>
                    <TableCell>{item.exchangeCommission}</TableCell>
                    <TableCell>{item.fiatConversion}</TableCell>
                    <TableCell style={{ color: blokkoColors.primary }}>{item.amountDue}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
  
          {reportData.map((item, index) => (
            <Accordion key={index}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>ISO: {item.isoName} - {item.isoID}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {item.holding.map((holding, holdingIndex) => (
                  <Accordion key={holdingIndex}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>{holding.merchantHolding} - {holding.merchantHoldingID}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Transactions Count</TableCell>
                              <TableCell>Transactions Amount</TableCell>
                              <TableCell>Exchange Commission</TableCell>
                              <TableCell>Fiat Conversion</TableCell>
                              <TableCell>Amount Due</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>{holding.transactionsCount}</TableCell>
                              <TableCell>{holding.transactionsAmount}</TableCell>
                              <TableCell>{holding.exchangeCommission}</TableCell>
                              <TableCell>{holding.fiatConversion}</TableCell>
                              <TableCell style={{ color: blokkoColors.primary }}>{holding.amountDue}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
  
                      <Typography variant="h6" style={{ marginTop: "20px" }}>Groups</Typography>
                      {holding.groups.group.map((group, groupIndex) => (
                        <Accordion key={groupIndex}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>{group.merchantGroup} - {group.merchantGroupID}</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <TableContainer component={Paper}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Transactions Count</TableCell>
                                    <TableCell>Transactions Amount</TableCell>
                                    <TableCell>Exchange Commission</TableCell>
                                    <TableCell>Fiat Conversion</TableCell>
                                    <TableCell>Amount Due</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  <TableRow>
                                    <TableCell>{group.transactionsCount}</TableCell>
                                    <TableCell>{group.transactionsAmount}</TableCell>
                                    <TableCell>{group.exchangeCommission}</TableCell>
                                    <TableCell>{group.fiatConversion}</TableCell>
                                    <TableCell style={{ color: blokkoColors.primary }}>{group.amountDue}</TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </TableContainer>
  
                            <Typography variant="h6" style={{ marginTop: "20px" }}>Merchants</Typography>
                            {group.merchants.merchant.map((merchant, merchantIndex) => (
                              <Accordion key={merchantIndex}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Typography>{merchant.name} - {merchant.ID} (Store: {merchant.store})</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <TableContainer component={Paper}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Transactions Count</TableCell>
                                          <TableCell>Transactions Amount</TableCell>
                                          <TableCell>Exchange Commission</TableCell>
                                          <TableCell>Fiat Conversion</TableCell>
                                          <TableCell>Amount Due</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        <TableRow>
                                          <TableCell>{merchant.transactionsCount}</TableCell>
                                          <TableCell>{merchant.transactionsAmount}</TableCell>
                                          <TableCell>{merchant.exchangeCommission}</TableCell>
                                          <TableCell>{merchant.fiatConversion}</TableCell>
                                          <TableCell style={{ color: blokkoColors.primary }}>{merchant.amountDue}</TableCell>
                                        </TableRow>
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
  
                                  <Typography variant="h6" style={{ marginTop: "20px" }}>Terminals</Typography>
                                  {merchant.terminals.terminal.map((terminal, terminalIndex) => (
                                    <TableContainer component={Paper} key={terminalIndex} style={{ marginBottom: "20px" }}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Terminal ID</TableCell>
                                            <TableCell>Exchange</TableCell>
                                            <TableCell>Transactions Count</TableCell>
                                            <TableCell>Transactions Amount</TableCell>
                                            <TableCell>Exchange Commission</TableCell>
                                            <TableCell>Fiat Conversion</TableCell>
                                            <TableCell>Amount Due</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          <TableRow>
                                            <TableCell>{terminal.terminalID}</TableCell>
                                            <TableCell>{terminal.exchangeID}</TableCell>
                                            <TableCell>{terminal.transactionsCount}</TableCell>
                                            <TableCell>{terminal.transactionsAmount}</TableCell>
                                            <TableCell>{terminal.exchangeCommission}</TableCell>
                                            <TableCell>{terminal.fiatConversion}</TableCell>
                                            <TableCell style={{ color: blokkoColors.primary }}>{terminal.amountDue}</TableCell>
                                          </TableRow>
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  ))}
                                </AccordionDetails>
                              </Accordion>
                            ))}
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </AccordionDetails>
      </Accordion>
    )
  };

  return (
    <>
      <LoadingBackdrop loading={loading} retryCount={retryCount} />

      <form onSubmit={handleSubmit} style={{ margin: "20px 0" }}>
        <Grid container spacing={3}>        
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled> 
              <InputLabel id="currency-label">Currency Code</InputLabel>
              <Select
                labelId="currency-label"
                id="currency"
                value={currency || ""}
                label="Currency Code"
                onChange={(event) => setCurrency(event.target.value)}
                required
              >
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="BRL">BRL</MenuItem>
                <MenuItem value="MXN">MXN</MenuItem>
                <MenuItem value="COP">COP</MenuItem>
                <MenuItem value="BOB">BOB</MenuItem>
                <MenuItem value="VES">VES</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="date"
              label="Date"
              type="date"
              value={date}
              onChange={handleDateChange}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: new Date(new Date().setDate(new Date().getDate() - 90))
                  .toISOString()
                  .split("T")[0], 
                max: new Date(new Date().setDate(new Date().getDate() - 1))
                  .toISOString()
                  .split("T")[0], 
              }}
              required
            />
          </Grid>
          <Grid item xs={12} textAlign={isMobile ? "center" : "left"} style={{ marginTop: "20px" }}>
            <Button 
              variant="contained" 
              type="submit" 
              size="large"
              disabled={loading} 
            >
              {loading ? 'GENERATING...' : 'GENERATE'}
            </Button>
            {reportData && (
              <Button
                variant="outlined"
                style={{ marginLeft: "10px" }}
                onClick={downloadCSV}
                disabled={hide || loading}
              >
                Download CSV
              </Button>
            )}
          </Grid>
        </Grid>
      </form>
      
      {!hide && !loading && reportData && renderHoldingsTable(reportData)}
    </>
  );
};

export default DailyIsoClearingSettlementReportForm;
